/**
 * Admin-only scoring config manager (PRD 8.2 / FR-104).
 *
 * A published scoring version is immutable - its weights, scoring_version and
 * objective never change once created, so any historical AssetScore stays
 * reproducible. What this screen lets an admin actually update is therefore
 * deliberately narrow: deactivate a version (without deleting the row audit
 * trails point at), edit its notes, or publish a brand new version.
 *
 * Every field that steers scoring carries a plain-language explanation from
 * the API (`/admin/scoring-configs/defaults`), revealed on hover (or focus)
 * over the "i" badge next to its label, so an admin changing a number can
 * see what it actually affects without the form being wall-to-wall text.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useIdentity } from '../hooks/useSession'
import type { ScoringConfig, ScoringConfigDefaults } from '../lib/types'
import { percent, shortDate, titleCase } from '../lib/format'
import { Callout, ErrorCallout } from '../components/Callout'
import { Chip } from '../components/Chips'
import { InfoTip } from '../components/InfoTip'
import { Skeleton } from '../components/Metric'

function ConfigRow({
  config,
  descriptions,
}: {
  config: ScoringConfig
  descriptions?: ScoringConfigDefaults
}) {
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState(config.notes ?? '')

  const update = useMutation({
    mutationFn: (body: { is_active?: boolean; notes?: string | null }) =>
      api.updateScoringConfig(config.id, body),
    onSuccess: (result) =>
      queryClient.setQueryData<ScoringConfig[]>(['scoring-configs'], (rows) =>
        (rows ?? []).map((r) => (r.id === result.id ? result : r)),
      ),
  })

  return (
    <tr>
      <td className="mono">
        {config.scoring_version}
        <div className="tiny faint">{shortDate(config.created_at)}</div>
      </td>
      <td className="small">{titleCase(config.objective)}</td>
      <td className="pill-row">
        {Object.entries(config.weights)
          .filter(([, weight]) => weight > 0)
          .sort((a, b) => b[1] - a[1])
          .map(([component, weight]) => (
            <Chip key={component} title={descriptions?.component_descriptions[component]}>
              {titleCase(component)} {percent(weight)}
            </Chip>
          ))}
      </td>
      <td
        className="small muted"
        title={descriptions?.missing_feature_policy_descriptions[config.missing_feature_policy]}
      >
        {config.missing_feature_policy.replace(/_/g, ' ')}
      </td>
      <td>
        <div className="row tight nowrap" style={{ gap: 6 }}>
          <label className="row tight nowrap" style={{ gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={config.is_active}
              disabled={update.isPending}
              onChange={(e) => update.mutate({ is_active: e.target.checked })}
            />
            {config.is_active ? (
              <Chip tone="ok">Active</Chip>
            ) : (
              <Chip tone="neutral">Inactive</Chip>
            )}
          </label>
          <InfoTip text="Whether this version can be picked by new recommendation runs. Turning it off does not delete the version - historical scores that used it stay valid." />
        </div>
      </td>
      <td className="small" style={{ minWidth: 220 }}>
        <textarea
          rows={2}
          value={notes}
          disabled={update.isPending}
          placeholder="Why this version exists, what changed, who approved it"
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => notes !== (config.notes ?? '') && update.mutate({ notes: notes || null })}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </td>
    </tr>
  )
}

function PublishForm({ defaults }: { defaults: ScoringConfigDefaults }) {
  const queryClient = useQueryClient()

  const [scoringVersion, setScoringVersion] = useState('')
  const [objective, setObjective] = useState('default')
  const [missingFeaturePolicy, setMissingFeaturePolicy] = useState('redistribute')
  const [notes, setNotes] = useState('')
  const [weights, setWeights] = useState<Record<string, number>>({ ...defaults.default_weights })

  const create = useMutation({
    mutationFn: () =>
      api.createScoringConfig({
        scoring_version: scoringVersion.trim(),
        objective: objective.trim() || 'default',
        weights,
        missing_feature_policy: missingFeaturePolicy,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-configs'] })
      setScoringVersion('')
      setNotes('')
      setWeights({ ...defaults.default_weights })
    },
  })

  const weightSum = Object.values(weights).reduce((a, b) => a + b, 0)

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="grow">Publish a new scoring version</h2>
        <span className="tiny faint">Weights are rescaled to sum to 1 on save</span>
      </div>
      <div className="card-body stack">
        {create.isError ? <ErrorCallout error={create.error} /> : null}
        <div className="row" style={{ gap: 12 }}>
          <label className="field grow">
            <span className="row tight nowrap" style={{ gap: 4 }}>
              Scoring version
              <InfoTip text="A unique label for this weight set, e.g. 1.1.0. Immutable once published - used to reproduce every historical score exactly against the config that produced it." />
            </span>
            <input
              placeholder="e.g. 1.1.0"
              value={scoringVersion}
              onChange={(e) => setScoringVersion(e.target.value)}
            />
          </label>
          <label className="field grow">
            <span className="row tight nowrap" style={{ gap: 4 }}>
              Objective
              <InfoTip text="Which campaign objective this weight set is tuned for (e.g. default, awareness, footfall). A run matches its brief's objective to a config with the same objective, falling back to 'default' if none matches." />
            </span>
            <input value={objective} onChange={(e) => setObjective(e.target.value)} />
          </label>
        </div>

        <div>
          <label className="field">
            <span className="row tight nowrap" style={{ gap: 4 }}>
              Missing-feature policy
              <InfoTip text="What happens when a scoring component has no data for an asset." />
            </span>
            <select
              value={missingFeaturePolicy}
              onChange={(e) => setMissingFeaturePolicy(e.target.value)}
            >
              {defaults.missing_feature_policies.map((p) => (
                <option key={p} value={p}>
                  {p.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <h4>
            Component weights{' '}
            <span className={`tiny ${Math.abs(weightSum - 1) < 0.01 ? 'faint' : 'warn'}`}>
              sum {weightSum.toFixed(2)}
            </span>
          </h4>
          <div className="grid two tight">
            {Object.entries(defaults.component_labels).map(([key, label]) => (
              <label key={key} className="field">
                <span className="row tight nowrap" style={{ gap: 4 }}>
                  {label}
                  <InfoTip text={defaults.component_descriptions[key]} />
                </span>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={weights[key] ?? 0}
                  onChange={(e) => setWeights({ ...weights, [key]: Number(e.target.value) || 0 })}
                />
              </label>
            ))}
          </div>
        </div>

        <label className="field">
          <span className="row tight nowrap" style={{ gap: 4 }}>
            Notes
            <InfoTip text="Free-text context for other admins: why this version exists, what changed from the previous one, who approved it. Shown alongside the version wherever it's listed." />
          </span>
          <textarea
            rows={2}
            placeholder="Why this version, what changed, who signed off"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <div>
          <button
            className="primary"
            disabled={!scoringVersion.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Publishing…' : 'Publish version'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ScoringConfigAdmin() {
  const { me, loading: identityLoading } = useIdentity()
  const configs = useQuery({ queryKey: ['scoring-configs'], queryFn: api.scoringConfigs })
  const defaults = useQuery({
    queryKey: ['scoring-config-defaults'],
    queryFn: api.scoringConfigDefaults,
    enabled: !!me?.permissions.includes('admin:config'),
  })

  if (identityLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={4} />
        </div>
      </main>
    )
  }

  if (!me?.permissions.includes('admin:config')) {
    return (
      <main className="main">
        <div className="page">
          <Callout tone="danger" title="Admins only">
            Your role ({me?.role.replace(/_/g, ' ') ?? 'unknown'}) does not permit changes to
            scoring configuration.
          </Callout>
        </div>
      </main>
    )
  }

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Scoring configuration</h1>
            <div className="page-sub">
              Publish new scoring versions and control which ones are in play for tenant{' '}
              <span className="mono">{me.tenant_id}</span>.
            </div>
          </div>
        </div>

        <Callout tone="info" title="Published versions are immutable">
          A version's weights can never change once published (FR-104) - every historical score
          must stay reproducible against the exact config that produced it. To change weights,
          publish a new version number. Deactivating a version removes it from future runs
          without deleting the row. Hover the <span className="info-tip">i</span> next to any
          field for what it controls.
        </Callout>

        {defaults.isLoading ? (
          <Skeleton rows={3} />
        ) : defaults.isError || !defaults.data ? (
          <ErrorCallout error={defaults.error} onRetry={() => void defaults.refetch()} />
        ) : (
          <PublishForm defaults={defaults.data} />
        )}

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Existing versions</h2>
          </div>
          {configs.isLoading ? (
            <div className="card-body">
              <Skeleton rows={4} />
            </div>
          ) : configs.isError ? (
            <div className="card-body">
              <ErrorCallout error={configs.error} onRetry={() => void configs.refetch()} />
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Version
                        <InfoTip text="A unique, immutable label for this weight set." />
                      </span>
                    </th>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Objective
                        <InfoTip text="Which campaign objective this weight set is tuned for." />
                      </span>
                    </th>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Weights
                        <InfoTip text="How much each factor counts toward an asset's total score. Hover a weight chip for what it measures." />
                      </span>
                    </th>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Missing-feature policy
                        <InfoTip text="What happens when a scoring component has no data for an asset." />
                      </span>
                    </th>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Active
                        <InfoTip text="Whether new recommendation runs can pick this version. Deactivating keeps history reproducible." />
                      </span>
                    </th>
                    <th>
                      <span className="row tight nowrap" style={{ gap: 4 }}>
                        Notes
                        <InfoTip text="Free-text context for other admins about this version." />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(configs.data ?? []).map((config) => (
                    <ConfigRow key={config.id} config={config} descriptions={defaults.data} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
